import { ArrowLeft } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { useLang } from '@/context/LanguageContext';

function LegalLayout({ title, children }) {
  const { lang } = useLang();
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEOHead title={`${title} | Contractors of South Florida`} description={title} url={typeof window !== 'undefined' ? window.location.pathname : '/'} lang={lang} />
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
        <Link to={lang === 'es' ? '/es' : '/'} className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors mb-12">
          <ArrowLeft size={16} /> {lang === 'es' ? 'Volver al inicio' : 'Back to Home'}
        </Link>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mb-4">{title}</h1>
        <div className="w-14 h-[3px] bg-[#E31937] mb-10" />
        <div className="prose prose-invert max-w-none space-y-6 text-[#A3A3A3] leading-relaxed text-sm">
          {children}
        </div>
      </div>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p className="text-xs text-[#666] mb-6">Effective date: October 2026</p>

      <Section t="">
        <p>Contractors of South Florida ("CSFL", "we", "us") operates contractorsfl.com and helps homeowners solve their home improvement problems by matching them with licensed, insured contractors across Miami-Dade, Broward, and Palm Beach counties. This policy explains what information we collect, how we use it, and your choices.</p>
        <p className="mt-3"><strong className="text-white">Contact:</strong> 305-904-8567 · contractorsfl.com</p>
      </Section>

      <Section t="Information We Collect">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Contact details you provide:</strong> name, phone number, email address, ZIP code.</li>
          <li><strong className="text-white">Project details:</strong> the type of job you need, whether you own the home, and how soon you need the work done.</li>
          <li><strong className="text-white">Website usage:</strong> basic analytics (pages visited, device type) used to improve the site.</li>
        </ul>
      </Section>

      <Section t="How We Use Your Information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To respond to your quote request and match you with a contractor for your project.</li>
          <li>To share your request with contractor partners in our network who may contact you about your project or purchase the lead to serve you. This is part of how our service works: we are a lead-generation network.</li>
          <li>To send you service updates and, with your consent, marketing messages by phone, text, or email.</li>
        </ul>
      </Section>

      <Section t="Marketing Calls and Texts (Consent)">
        <p className="mb-3">If you submit a quote request, you agree that Contractors of South Florida and our contractor partners may contact you by phone call or text message about your request and related services, including marketing messages that may be sent using an automatic dialing system or prerecorded voice.</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Consent is not a condition</strong> of receiving any quote or service.</li>
          <li>Message and data rates may apply. Message frequency varies.</li>
          <li>To opt out of texts, reply <strong className="text-white">STOP</strong> at any time; to opt back in, reply <strong className="text-white">START</strong>.</li>
          <li>To opt out of calls or emails, contact us at 305-904-8567.</li>
        </ul>
        <p className="mt-3">We keep a record of your consent (what you agreed to and when) as required by federal and Florida law.</p>
      </Section>

      <Section t="Information Sharing">
        <p className="mb-3">We share your information only as needed to serve you:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Contractor partners:</strong> businesses in our network that may fulfill your project or purchase your lead.</li>
          <li><strong className="text-white">Service providers:</strong> companies that help us operate the website and advertising (for example, Meta, when you submit a lead form on Facebook or Instagram).</li>
          <li><strong className="text-white">Legal:</strong> when required by law or to protect our rights.</li>
        </ul>
        <p className="mt-3">We do not sell your information for unrelated marketing purposes.</p>
      </Section>

      <Section t="Your Choices and Rights">
        <ul className="list-disc pl-6 space-y-2">
          <li>You may ask us to correct, update, or delete your personal information by calling 305-904-8567.</li>
          <li>Florida residents: you may request details about the information we hold about you and how it is shared, and ask us to delete it, subject to legal record-keeping requirements.</li>
          <li>You may opt out of marketing communications at any time as described above.</li>
        </ul>
      </Section>

      <Section t="Data Retention and Security">We keep quote and consent records as long as needed for the purposes above and to meet legal obligations. We use reasonable administrative, technical, and organizational measures to protect your information, but no system is perfectly secure.</Section>

      <Section t="Children">Our services are for adults. We do not knowingly collect information from children under 13.</Section>

      <Section t="Changes to This Policy">If we change this policy, we will post the updated version here with a new effective date.</Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms and Conditions">
      <Section t="1. Introduction">These Terms govern your use of Contractors of South Florida LLC website and services. If you disagree, please do not use our website or services.</Section>
      <Section t="2. Using Our Website">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Age Restriction:</strong> You must be at least 18 years old.</li>
          <li><strong className="text-white">Legal Use Only:</strong> You may not use our website for any illegal purpose.</li>
          <li><strong className="text-white">Changes:</strong> We may modify or remove content without notice.</li>
        </ul>
      </Section>
      <Section t="3. Lead Matching">By submitting a quote request, you understand your information may be shared with licensed contracting partners in our network who service your area. Contractors of South Florida acts as a matching service connecting homeowners with qualified professionals.</Section>
      <Section t="4. Intellectual Property">All content on our website is protected by copyright. You may not copy or distribute without permission.</Section>
      <Section t="5. No Guarantees">Information is for general knowledge. We do not guarantee accuracy or completeness.</Section>
      <Section t="6. Limitation of Liability">Contractors of South Florida LLC is not liable for any damages arising from use of our website or services.</Section>
      <Section t="7. Indemnification">You will protect Contractors of South Florida LLC from any claims arising from your use of our services.</Section>
      <Section t="8. Governing Law">These terms are governed by the laws of Palm Beach County, Florida.</Section>
      <Section t="9. Changes">We may change these terms at any time. Continued use means you accept updated terms.</Section>
    </LegalLayout>
  );
}

export function SmsTermsPage() {
  return (
    <LegalLayout title="SMS Terms of Service">
      <Section t="SMS Messaging Program">Contractors of South Florida LLC offers an SMS messaging program for project updates, appointment reminders, and payment notifications.</Section>
      <Section t="Opt-In">By checking the consent box on our contact or quote form, you agree to receive text messages from Contractors of South Florida and its matched licensed contracting partners.</Section>
      <Section t="Opt-Out (TCPA Compliance)">You can opt out at any time by replying STOP to any message. After opting out, you will receive one confirmation message and no further texts. You may also email sales@contractorsfl.com to request removal.</Section>
      <Section t="Help">Reply HELP for assistance, or contact sales@contractorsfl.com.</Section>
      <Section t="Message Frequency">Message frequency varies based on your project status. Typically 1-5 messages per month.</Section>
      <Section t="Costs">Message and data rates may apply. Check with your carrier.</Section>
      <Section t="Data Sharing">No mobile information will be shared with third parties or affiliates for marketing/promotional purposes. Text messaging opt-in data and consent will not be shared with any third parties.</Section>
      <Section t="Supported Carriers">Major US carriers are supported including AT&T, Verizon, T-Mobile, Sprint, and others.</Section>
      <Section t="Privacy">See our <a href="/privacy" className="text-[#E31937] underline">Privacy Policy</a> for data handling practices.</Section>
    </LegalLayout>
  );
}

function Section({ t, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-lg text-white mb-3">{t}</h2>
      {typeof children === 'string' ? <p>{children}</p> : children}
    </section>
  );
}
